import React, { useState, useEffect } from 'react';
import { PoleNote } from '../../types';
import { getErrorMessage } from '../../utils/errorMessages';
import './Dialog.css';
import './PoleNotesDialog.css';

interface PoleNotesDialogProps {
  isOpen: boolean;
  onClose: () => void;
  poleId: number;
  poleName: string;
  projectId: number;
}

const API_URL = process.env.REACT_APP_API_URL && process.env.REACT_APP_API_URL !== '' 
  ? process.env.REACT_APP_API_URL 
  : '/api';

export const PoleNotesDialog: React.FC<PoleNotesDialogProps> = ({
  isOpen,
  onClose,
  poleId,
  poleName,
  projectId
}) => {
  const [notes, setNotes] = useState<PoleNote[]>([]);
  const [newNote, setNewNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [editingText, setEditingText] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchNotes();
    }
  }, [isOpen, poleId]);

  const fetchNotes = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${API_URL}/projects/${projectId}/poles/${poleId}/notes`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Nepodarilo sa načítať poznámky');
      }

      const data = await response.json();
      setNotes(data);
    } catch (err: any) {
      setError(getErrorMessage(err, 'Nepodarilo sa načítať poznámky.'));
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${API_URL}/projects/${projectId}/poles/${poleId}/notes`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({ note: newNote.trim() }),
        }
      );

      if (!response.ok) {
        throw new Error('Nepodarilo sa pridať poznámku');
      }

      setNewNote('');
      await fetchNotes();
    } catch (err: any) {
      setError(getErrorMessage(err, 'Nepodarilo sa pridať poznámku.'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateNote = async (noteId: number) => {
    if (!editingText.trim()) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${API_URL}/projects/${projectId}/poles/${poleId}/notes/${noteId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({ note: editingText.trim() }),
        }
      );

      if (!response.ok) {
        throw new Error('Nepodarilo sa upraviť poznámku');
      }

      setEditingNoteId(null);
      setEditingText('');
      await fetchNotes();
    } catch (err: any) {
      setError(getErrorMessage(err, 'Nepodarilo sa upraviť poznámku.'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteNote = async (noteId: number) => {
    if (!window.confirm('Naozaj chcete vymazať túto poznámku?')) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${API_URL}/projects/${projectId}/poles/${poleId}/notes/${noteId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Nepodarilo sa vymazať poznámku');
      }

      await fetchNotes();
    } catch (err: any) {
      setError(getErrorMessage(err, 'Nepodarilo sa vymazať poznámku.'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('sk-SK', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!isOpen) return null;

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog pole-notes-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>📝 Poznámky ku stĺpiku: {poleName}</h2>
          <button className="dialog-close" onClick={onClose}>×</button>
        </div>

        <div className="dialog-content">
          {error && (
            <div className="error-message" style={{ marginBottom: '15px' }}>
              ❌ {error}
            </div>
          )}

          {/* Add new note */}
          <div className="add-note-section">
            <textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Napíšte novú poznámku..."
              rows={3}
              disabled={loading}
            />
            <button
              onClick={handleAddNote}
              className="btn-primary"
              disabled={loading || !newNote.trim()}
            >
              ➕ Pridať poznámku
            </button>
          </div>

          {/* Notes list */}
          <div className="notes-list">
            {loading && notes.length === 0 ? (
              <div className="loading-message">Načítava sa...</div>
            ) : notes.length === 0 ? (
              <div className="empty-message">
                Zatiaľ žiadne poznámky. Pridajte prvú poznámku vyššie.
              </div>
            ) : (
              notes.map((note) => (
                <div key={note.id} className="note-item">
                  <div className="note-header">
                    <div className="note-author">
                      <strong>{note.user_name || 'Neznámy používateľ'}</strong>
                      {note.user_email && (
                        <span className="note-email"> ({note.user_email})</span>
                      )}
                    </div>
                    <div className="note-date">
                      {formatDate(note.created_at)}
                      {note.updated_at && note.updated_at !== note.created_at && (
                        <span className="note-edited"> (upravené)</span>
                      )}
                    </div>
                  </div>

                  {editingNoteId === note.id ? (
                    <div className="note-edit">
                      <textarea
                        value={editingText}
                        onChange={(e) => setEditingText(e.target.value)}
                        rows={3}
                        disabled={loading}
                      />
                      <div className="note-edit-actions">
                        <button
                          onClick={() => handleUpdateNote(note.id)}
                          className="btn-primary btn-sm"
                          disabled={loading || !editingText.trim()}
                        >
                          💾 Uložiť
                        </button>
                        <button
                          onClick={() => {
                            setEditingNoteId(null);
                            setEditingText('');
                          }}
                          className="btn-secondary btn-sm"
                          disabled={loading}
                        >
                          Zrušiť
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="note-content">{note.note}</div>
                      <div className="note-actions">
                        <button
                          onClick={() => {
                            setEditingNoteId(note.id);
                            setEditingText(note.note);
                          }}
                          className="btn-icon"
                          title="Upraviť"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDeleteNote(note.id)}
                          className="btn-icon btn-danger"
                          title="Vymazať"
                        >
                          🗑️
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        <div className="dialog-footer">
          <button onClick={onClose} className="btn-secondary">
            Zavrieť
          </button>
        </div>
      </div>
    </div>
  );
};

